import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { 
  Box, Typography, TextField, Chip, Stack, Button, Dialog, DialogTitle, DialogContent,
  Tabs, Tab, CircularProgress, Snackbar, Alert, Badge
} from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../api/axios';
import ReframesDisplay from './storyboard/ReframesDisplay';
import OutcomesDisplay from './storyboard/OutcomesDisplay';
import StructureDisplay from './storyboard/StructureDisplay';
import CompetenciesDisplay from './storyboard/CompetenciesDisplay';
import RolesDisplay from './storyboard/RolesDisplay';
import EmptyState from './storyboard/EmptyState';
import { normalizeLearningOutcomesData } from '../utils/normalizeLearningOutcomes';
import {
  hasValidLearningOutcomes,
  hasValidReframesData,
  hasValidStructureData,
  hasValidCompetenciesData,
  isEloModuleComplete
} from '../utils/storyboardTabValidation';

function TabPanel({ children, value, index }) {
  const isActive = value === index;
  
  // CRITICAL: Add logging to verify panel visibility updates
  React.useEffect(() => {
    if (isActive) {

    }
  }, [isActive, value, index]);
  
  return (
    <Box
      role="tabpanel"
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      sx={{
        display: isActive ? 'flex' : 'none', // Only use display, not hidden
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto', // CRITICAL: Scrolling happens here!
        overflowX: 'hidden',
        position: 'relative',
        // Custom scrollbar styles
        '&::-webkit-scrollbar': {
          width: '10px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#e2e8f0',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#94a3b8',
          borderRadius: '10px',
          '&:hover': {
            background: '#64748b',
          },
        },
      }}
      onWheel={(e) => {
        // CRITICAL: Prevent React Flow from stealing scroll
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        // Prevent node dragging
        e.stopPropagation();
      }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      {children}
    </Box>
  );
}

const OUTCOME_TYPE_CORE_MESSAGE = 'core_message';
const OUTCOME_TYPE_LEARNING_OUTCOME = 'learning_outcome';
const OUTCOME_TYPE_UNKNOWN = 'unknown';
const OUTCOME_TYPE_NONE = 'none';

function detectOutcomeType(payload) {
  if (!payload) return OUTCOME_TYPE_NONE;
  if (payload.outcome_type && typeof payload.outcome_type === 'string') {
    return payload.outcome_type;
  }
  const outcomes = Array.isArray(payload.outcomes) ? payload.outcomes : [];
  const hasCoreMessage = outcomes.some(outcome => outcome && typeof outcome.core_message === 'string' && outcome.core_message.trim().length > 0);
  if (hasCoreMessage) return OUTCOME_TYPE_CORE_MESSAGE;
  const hasStatement = outcomes.some(outcome => outcome && ((typeof outcome.statement === 'string' && outcome.statement.trim().length > 0) || (typeof outcome.title === 'string' && outcome.title.trim().length > 0)));
  if (hasStatement || outcomes.length > 0) return OUTCOME_TYPE_LEARNING_OUTCOME;
  return OUTCOME_TYPE_UNKNOWN;
}

function formatOutcomeTypeLabel(type) {
  if (!type) return 'Unknown Outcome';
  switch (type) {
    case OUTCOME_TYPE_CORE_MESSAGE:
      return 'Core Message';
    case OUTCOME_TYPE_LEARNING_OUTCOME:
      return 'Learning Outcome';
    case OUTCOME_TYPE_NONE:
      return 'No Outcome';
    case OUTCOME_TYPE_UNKNOWN:
      return 'Unknown Outcome';
    default:
      return type.replace(/_/g, ' ');
  }
}

// Global lock to prevent multiple modules from generating simultaneously
const globalGenerationLock = { current: false };

// Reset generation lock on page load (in case of refresh)
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    globalGenerationLock.current = false;

  });
}

// VERSION: 2025-01-17-002 - Fixed component remounting issue
export default function StoryboardNode({ id, data, selected }) {
  // Extract callbacks from data (passed via StoryBoardFlow to prevent remounting)
  const onChange = data?._onChange;
  const assigned = data?._assigned;
  const onOpenTasks = data?._onOpenTasks;
  const onModuleComplete = data?._onModuleComplete;
  const isReadOnly = data?._isReadOnly || false;
  
  const d = useMemo(() => data || {}, [data]);
  const moduleIndex = d._moduleIndex || 1;
  const [activeTab, setActiveTab] = useState(0);
  
  // CRITICAL: Log when component mounts to verify new code is loaded
  useEffect(() => {

    return () => {

    };
  }, [id, moduleIndex]);
  const [openJson, setOpenJson] = useState(false);
  const [openAIJson, setOpenAIJson] = useState(false);
  const [aiJsonPreview, setAIJsonPreview] = useState(null);
  const [generatingReframes, setGeneratingReframes] = useState(false);
  const [generatingReframeConcepts, setGeneratingReframeConcepts] = useState(false);
  const [generatingReframeWording, setGeneratingReframeWording] = useState(false);
  const [generatingCoreOutcomes, setGeneratingCoreOutcomes] = useState(false);
  const [generatingLearningOutcomes, setGeneratingLearningOutcomes] = useState(false);
  const [generatingStructure, setGeneratingStructure] = useState(false);
  const [generatingCompetencies, setGeneratingCompetencies] = useState(false);
  const [generatingRoles, setGeneratingRoles] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const autoGenStartedRef = useRef(false); // Prevent duplicate auto-generation
  
  // Edit tracking state
  const [hasEdits, setHasEdits] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedData, setEditedData] = useState({
    core_outcomes: null,
    reframes: null,
    learning_outcomes: null,
    structure: null,
    competencies: null,
    roles: null
  });

  // For orientation nodes, ensure static content is available as fallback
  const isOrientationNode = d.is_orientation === true || d.uses_curriculum_doc === true;
  const isEisaNode = d.is_eisa === true;
  const ORIENTATION_STATIC_CONTENT_FALLBACK = {
    core_outcomes: {
      outcome_type: 'core_message',
      core_message: 'Orientation is the foundation of successful preparation and organisation of my studies',
      supporting_points: [
        'Enhance students\' ability to manage stress, time, and study assertiveness.',
        'Planning for core study components.',
        'Deepening academic rigour and ethical utilisation of AI technologies.'
      ],
      validation: { isValid: true, detected: 'core_message' }
    },
    learning_outcomes: {
      outcomes: [
        { statement: 'Obtain a learning and teaching overview.', type: 'learning_outcome' },
        { statement: 'Obtain a programme overview.', type: 'learning_outcome' }
      ],
      validation: { isValid: true, detected: 'learning_outcome' }
    },
    reframes: {
      reframes: [{
        type: 'perspective_shift',
        statement: 'My learning efficacy is deeply influenced by my study overviews.',
        why: 'Understanding the learning and teaching structure enables effective study planning',
        next_move: 'Engage with orientation materials to build foundational knowledge',
        evidence_nodes: []
      }],
      coverage: { nodes_considered: [], dominant_themes: ['Orientation', 'Study Planning'] }
    },
    structure: {
      description: 'The Orientation module is structured to provide two critical insights: the learning and teaching overview and the programme overview.',
      chapters: [
        { title: 'CH01 The CPSLearn System', order: 1 },
        { title: 'CH02 Academic Integrity', order: 2 },
        { title: 'CH03 Ethical use of AI', order: 3 },
        { title: 'CH04 Assessment Practice', order: 4 },
        { title: 'CH05 Student Support', order: 5 },
        { title: 'CH06 The Qualification (QCTO/CHE)', order: 6 },
        { title: 'CH07 The Journey Map', order: 7 },
        { title: 'CH08 The Work Experience Journey', order: 8 },
        { title: 'CH09 My Workplace Mentor Contract', order: 9 }
      ]
    }
  };
  
  const EISA_STATIC_CONTENT_FALLBACK = {
    core_outcomes: {
      outcome_type: 'core_message',
      core_message: 'Preparing and building my knowledge, and practical skills capacity, enabling a successful EISA attempt.',
      supporting_points: [
        'Review of Knowledge, Practical and Work Experience components and their assessments',
        'Study rules and preparation for the external exam',
        'AI formative assessment feedback',
        'EISA Preparation',
        'Mock Examination and support'
      ],
      validation: { isValid: true, detected: 'core_message' }
    },
    learning_outcomes: {
      outcomes: [
        { statement: 'Understand the purpose of EISA.', type: 'learning_outcome' },
        { statement: 'Comprehend EISA requirements and process to follow for application.', type: 'learning_outcome' }
      ],
      validation: { isValid: true, detected: 'learning_outcome' }
    },
    reframes: {
      reframes: [{
        type: 'perspective_shift',
        statement: 'Revision and Practice',
        why: 'Systematic revision and practice are essential for successful EISA preparation',
        next_move: 'Engage in comprehensive review and mock examinations',
        evidence_nodes: []
      }],
      coverage: { nodes_considered: [], dominant_themes: ['EISA Preparation', 'Assessment Readiness'] }
    },
    structure: {
      description: 'The EISA Preparation module is structured to provide comprehensive preparation for the External Integrated Summative Assessment through review, practice, and assessment readiness.',
      chapters: [
        { title: 'Review of Knowledge, Practical and Work Experience components and their assessments', order: 1 },
        { title: 'Study rules and preparation for the external exam', order: 2 },
        { title: 'AI formative assessment feedback', order: 3 },
        { title: 'EISA Preparation', order: 4 },
        { title: 'Mock Examination and support', order: 5 }
      ]
    }
  };
  
  const coreOutcomesData = useMemo(() => {
    if (isOrientationNode && !d.ai_core_outcomes) {
      return ORIENTATION_STATIC_CONTENT_FALLBACK.core_outcomes;
    }
    if (isEisaNode && !d.ai_core_outcomes) {
      return EISA_STATIC_CONTENT_FALLBACK.core_outcomes;
    }
    return d.ai_core_outcomes || null;
  }, [d, isOrientationNode, isEisaNode]);
  
  const detectedCoreOutcomeType = useMemo(() => {
    if (coreOutcomesData?.validation?.detected) {
      return coreOutcomesData.validation.detected;
    }
    return detectOutcomeType(coreOutcomesData);
  }, [coreOutcomesData]);
  
  // Additional check: ensure data actually has core_message fields, not just statement fields
  const hasActualCoreMessage = useMemo(() => {
    if (!coreOutcomesData) return false;
    // Check if core_message exists directly (static content format)
    if (coreOutcomesData.core_message && typeof coreOutcomesData.core_message === 'string' && coreOutcomesData.core_message.trim().length > 0) {
      return true;
    }
    // Check if core_message exists in outcomes array (AI-generated format)
    const outcomes = coreOutcomesData.outcomes || [];
    if (outcomes.length === 0) return false;
    // Check if any outcome has core_message field
    return outcomes.some(outcome => outcome && typeof outcome.core_message === 'string' && outcome.core_message.trim().length > 0);
  }, [coreOutcomesData]);
  
  const shouldRenderCoreMessage = (detectedCoreOutcomeType === OUTCOME_TYPE_CORE_MESSAGE && hasActualCoreMessage) || 
                                  (coreOutcomesData && coreOutcomesData.core_message && !coreOutcomesData.outcomes);
  const coreOutcomeLabel = formatOutcomeTypeLabel(detectedCoreOutcomeType);
  
  const learningOutcomesData = useMemo(() => {
    if (isOrientationNode && !d.ai_learning_outcomes) {
      return ORIENTATION_STATIC_CONTENT_FALLBACK.learning_outcomes;
    }
    if (isEisaNode && !d.ai_learning_outcomes) {
      return EISA_STATIC_CONTENT_FALLBACK.learning_outcomes;
    }
    return normalizeLearningOutcomesData(d.ai_learning_outcomes) || null;
  }, [d, isOrientationNode, isEisaNode]);
  
  const structureData = useMemo(() => {
    if (isOrientationNode && !d.ai_structure) {
      return ORIENTATION_STATIC_CONTENT_FALLBACK.structure;
    }
    if (isEisaNode && !d.ai_structure) {
      return EISA_STATIC_CONTENT_FALLBACK.structure;
    }
    return d.ai_structure || null;
  }, [d, isOrientationNode, isEisaNode]);
  
  const reframesData = useMemo(() => {
    if (isOrientationNode && !d.ai_reframes) {
      return ORIENTATION_STATIC_CONTENT_FALLBACK.reframes;
    }
    if (isEisaNode && !d.ai_reframes) {
      return EISA_STATIC_CONTENT_FALLBACK.reframes;
    }
    return d.ai_reframes || null;
  }, [d, isOrientationNode, isEisaNode]);
  
  const competenciesData = useMemo(() => d.ai_competencies || null, [d]);
  const rolesData = useMemo(() => d.ai_roles || null, [d]);

  // Reset auto-gen ref when all data is cleared (allows regeneration)
  useEffect(() => {
    const allDataCleared = !coreOutcomesData && !reframesData && !learningOutcomesData && 
                          !structureData && !competenciesData && !rolesData;
    if (allDataCleared && autoGenStartedRef.current) {

      autoGenStartedRef.current = false;
    }
  }, [coreOutcomesData, reframesData, learningOutcomesData, structureData, competenciesData, rolesData, d._dbId]);

  // Auto-generate ALL missing content sequentially for ALL modules
  // NEW FULL SEQUENCE: Core Outcomes → Learning Outcomes → Reframes → Structure → Competencies → Roles (Orientation only)
  useEffect(() => {
    if (!d._dbId || !d._qualificationId) {

      return;
    }
    
    // CRITICAL: Prevent duplicate execution - check FIRST before any other logic
    // Allow re-run when parent explicitly triggers another generation pass
    if (d._triggerAutoGen) {
      autoGenStartedRef.current = false;
    }
    if (autoGenStartedRef.current) {

      return;
    }
    
    // CRITICAL: Check GLOBAL lock - if locked, wait and retry later
    if (globalGenerationLock.current) {

      const retryTimer = setTimeout(() => {

        // Force re-check by not setting autoGenStartedRef yet
        if (!globalGenerationLock.current) {
          // Lock is free now, trigger useEffect again via data change
          if (typeof onChange === 'function') {
            onChange(id, { ...d, _retryAutoGen: Date.now() });
          }
        }
      }, 5000);
      return () => clearTimeout(retryTimer);
    }
    
    const isOrientation = d.is_orientation === true || d.uses_curriculum_doc === true;
    const isEisa = d.is_eisa === true;

    // Only orientation/EISA start on mount; ELO modules wait for chain trigger from previous module
    if (!d._triggerAutoGen && !d._retryAutoGen && !isOrientation && !isEisa) {
      return;
    }
    
    // ORIENTATION AND EISA NODES: Never make AI calls - always use static content
    // Mark as complete immediately to allow next node to start
    if (isOrientation || isEisa) {
      // If static content doesn't exist, it should be created by the backend on node creation
      // But we never trigger AI generation for orientation or EISA nodes
      autoGenStartedRef.current = true; // Mark as done - no AI calls for orientation/EISA
      // Release lock immediately so next node can start
      globalGenerationLock.current = false;
      // Notify parent that this module is complete so it can advance to next
      if (typeof onModuleComplete === 'function') {
        setTimeout(() => {
          onModuleComplete(id, d._moduleIndex);
        }, 100); // Small delay to ensure state updates
      }
      return; // Exit immediately - no AI generation for orientation/EISA nodes
    }
    
    // Check if all content already exists - skip auto-gen if complete, but still advance chain
    const moduleComplete = isEloModuleComplete({
      coreOutcomesData,
      learningOutcomesData,
      reframesData,
      structureData,
      competenciesData,
      hasActualCoreMessage
    });
    if (moduleComplete) {
      autoGenStartedRef.current = true;
      globalGenerationLock.current = false;
      if (typeof onModuleComplete === 'function') {
        setTimeout(() => {
          onModuleComplete(id, d._moduleIndex);
        }, 100);
      }
      return;
    }
    
    // Acquire GLOBAL lock - only ONE module can generate at a time
    // This ensures nodes generate sequentially, starting with the first ELO node (after orientation)

    globalGenerationLock.current = true;
    
    // Mark as started IMMEDIATELY to prevent race conditions

    autoGenStartedRef.current = true;
    
    const autoGenerateAll = async () => {
      const totalSteps = 5; // Always 5 for ELO modules (no orientation in this path)
      const moduleType = 'ELO Module';

      // Check if backend is available before starting (with retry)
      // Use root endpoint directly (not through api instance which adds /api prefix)
      let backendAvailable = false;
      console.log('[autoGenerateAll] Starting backend health check...');
      for (let retry = 0; retry < 3; retry++) {
        try {
          const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
          const rootUrl = baseUrl.replace('/api', '').replace(/\/$/, ''); // Remove /api and trailing slash
          console.log(`[autoGenerateAll] Health check attempt ${retry + 1}/3: ${rootUrl}/`);
          const response = await fetch(`${rootUrl}/`, { method: 'GET' });
          console.log(`[autoGenerateAll] Health check response: ${response.status} ${response.statusText}`);
          if (response.ok || response.status === 401) { // 401 is OK, means backend is up
            backendAvailable = true;
            console.log('[autoGenerateAll] Backend health check passed');
            break;
          }
        } catch (healthError) {
          console.error(`[autoGenerateAll] Health check attempt ${retry + 1} failed:`, healthError);
          const isConnectionError = !healthError.response || healthError.code === 'ECONNREFUSED' || healthError.message?.includes('Network Error') || healthError.message?.includes('ERR_NETWORK') || healthError.message?.includes('Failed to fetch');
          if (isConnectionError && retry < 2) {
            // Wait a bit and retry
            console.log(`[autoGenerateAll] Retrying health check in ${1000 * (retry + 1)}ms...`);
            await new Promise(r => setTimeout(r, 1000 * (retry + 1)));
            continue;
          } else if (isConnectionError) {
            console.error('[autoGenerateAll] Backend unavailable after all retries');
            setSnackbar({ 
              open: true, 
              message: '❌ Backend server not available. Please ensure the backend is running (npm run dev in backend folder).', 
              severity: 'error',
              autoHideDuration: 15000
            });
            globalGenerationLock.current = false;
            return; // Exit early if backend unavailable after retries
          }
        }
      }
      
      if (!backendAvailable) {
        console.error('[autoGenerateAll] Backend health check failed, aborting');
        globalGenerationLock.current = false;
        return;
      }
      
      console.log('[autoGenerateAll] Backend confirmed available, proceeding with generation');
      
      const runStep = async (label, fn) => {
        try {
          await fn();
        } catch (stepError) {
          console.error(`[autoGenerateAll] ${label} failed:`, stepError);
          setSnackbar({
            open: true,
            message: `⚠️ ${label} failed — continuing with remaining tabs...`,
            severity: 'warning',
            autoHideDuration: 6000
          });
        }
      };
      
      // Show overall progress toast
      setSnackbar({ 
        open: true, 
        message: `🚀 Starting automatic generation for ${moduleType}...`, 
        severity: 'info',
        autoHideDuration: 3000
      });
      
      await new Promise(r => setTimeout(r, 2000)); // Wait 2s after page load
      
      try {
        // NEW SEQUENCE: Core Message → Learning Outcomes → Reframes → Structure → Competencies
        
        // STEP 1: Core Message
        if (!hasActualCoreMessage && !generatingCoreOutcomes) {
          await runStep('Core Message', async () => {
            setSnackbar({ open: true, message: `📝 Step 1/${totalSteps}: Core Message (30-60s)`, severity: 'info', autoHideDuration: 90000 });
            await handleGenerateCoreOutcomes();
            await new Promise(r => setTimeout(r, 5000));
          });
        }
        
        // STEP 2: Learning Outcomes — validate content, not just presence of object
        if (!hasValidLearningOutcomes(d.ai_learning_outcomes) && !generatingLearningOutcomes) {
          await runStep('Learning Outcomes', async () => {
            setSnackbar({ open: true, message: `🎯 Step 2/${totalSteps}: Learning Outcomes (30-60s)`, severity: 'info', autoHideDuration: 90000 });
            await handleGenerateLearningOutcomes();
            await new Promise(r => setTimeout(r, 5000));
          });
        }
        
        // STEP 3: Reframe Concepts
        const reframeStage = d.ai_reframes?.generation_stage || 'none';
        const hasConcepts = reframeStage === 'concepts' || reframeStage === 'wording' || reframeStage === 'complete';
        const hasCompleteWording = reframeStage === 'complete' &&
          (d.ai_reframes?.reframes || []).every(rf => rf.wording?.statement || rf.statement);
        
        if (!hasValidReframesData(reframesData) && !hasConcepts && !generatingReframeConcepts && !generatingReframes) {
          await runStep('Reframe Concepts', async () => {
            setSnackbar({ open: true, message: `💡 Step 3/${totalSteps}: Reframe Concepts (15-30s)`, severity: 'info', autoHideDuration: 90000 });
            await handleGenerateReframeConcepts();
            await new Promise(r => setTimeout(r, 3000));
          });
        }
        
        // STEP 3b: Reframe Wording
        if (hasConcepts && !hasCompleteWording && !hasValidReframesData(reframesData) && !generatingReframeWording && !generatingReframes) {
          await runStep('Reframe Wording', async () => {
            setSnackbar({ open: true, message: `✨ Step 3b/${totalSteps}: Reframe Wording (15-30s)`, severity: 'info', autoHideDuration: 90000 });
            await handleGenerateReframeWording();
            await new Promise(r => setTimeout(r, 3000));
          });
        }
        
        // STEP 4: Structure
        if (!hasValidStructureData(structureData) && !generatingStructure) {
          await runStep('Structure', async () => {
            setSnackbar({ open: true, message: `🏗️ Step 4/${totalSteps}: Structure (30-60s)`, severity: 'info', autoHideDuration: 90000 });
            await handleGenerateStructure();
            await new Promise(r => setTimeout(r, 5000));
          });
        }
        
        // STEP 5: Competencies
        if (!isOrientation && !hasValidCompetenciesData(competenciesData) && !generatingCompetencies) {
          await runStep('Competencies', async () => {
            setSnackbar({ open: true, message: `⚡ Step 5/${totalSteps}: Competencies (30-60s)`, severity: 'info', autoHideDuration: 90000 });
            await handleGenerateCompetencies();
            await new Promise(r => setTimeout(r, 5000));
          });
        }

        setSnackbar({ 
          open: true, 
          message: `✅ ${d.title || 'Module'} generation pass complete. Moving to next module...`, 
          severity: 'success',
          autoHideDuration: 3000
        });
        
      } catch (error) {
        const isConnectionError = error.message === 'Backend unavailable' || 
                                  error.code === 'ECONNREFUSED' || 
                                  error.message?.includes('Network Error') ||
                                  error.message?.includes('ERR_NETWORK');

        if (isConnectionError) {
          setSnackbar({ 
            open: true, 
            message: '❌ Backend server unavailable. Auto-generation stopped. Please start the backend server (npm run dev in backend folder).', 
            severity: 'error',
            autoHideDuration: 15000
          });
          globalGenerationLock.current = false;
          return;
        }

        setSnackbar({ 
          open: true, 
          message: `⚠️ Generation error: ${error.message || 'Some tabs incomplete'}. Continuing to next module...`, 
          severity: 'warning',
          autoHideDuration: 6000
        });
      } finally {
        // Always release lock and advance to next module (unless backend unavailable above)
        globalGenerationLock.current = false;
        if (typeof onModuleComplete === 'function') {
          setTimeout(() => {
            onModuleComplete(id, d._moduleIndex);
          }, 2000);
        }
      }
    };
    
    autoGenerateAll();
  }, [d._triggerAutoGen, d._retryAutoGen]); // Re-run when parent triggers OR retry after lock

  const handleField = (key) => (e) => {
    const value = e && e.target ? e.target.value : e;
    if (typeof onChange === 'function') onChange(id, { ...d, [key]: value });
  };

  // Handle data edits with user tracking - AUTO-SAVE immediately
  const handleDataEdit = useCallback(async (field, newData) => {

    
    if (!d._dbId || !d._qualificationId) return;
    
    try {
      // Prepare update with user_edited flag
      const update = {
        [field === 'core_outcomes' ? 'ai_core_outcomes' : 
         field === 'reframes' ? 'ai_reframes' :
         field === 'learning_outcomes' ? 'ai_learning_outcomes' :
         field === 'structure' ? 'ai_structure' :
         field === 'competencies' ? 'ai_competencies' :
         field === 'roles' ? 'ai_roles' : field]: {
          ...newData,
          user_edited: true,
          last_edited_at: new Date().toISOString()
        }
      };

      // Save to backend immediately
      await api.put(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}`, update);
      
      // Update local state
      if (typeof onChange === 'function') {
        onChange(id, { ...d, ...update });
      }
      
      setSnackbar({ open: true, message: '✅ Changes saved automatically!', severity: 'success', autoHideDuration: 2000 });

    } catch (error) {

      setSnackbar({ open: true, message: '❌ Failed to save changes', severity: 'error' });
    }
  }, [d, id, onChange]);

  // Save all edited data to backend
  const handleSaveModule = useCallback(async () => {
    if (!d._dbId || !d._qualificationId) return;
    
    setSaving(true);
    try {
      // Prepare update payload with edited data
      const updates = {};
      
      if (editedData.core_outcomes) {
        updates.ai_core_outcomes = {
          ...editedData.core_outcomes,
          user_edited: true,
          last_edited_at: new Date().toISOString()
        };
      }
      if (editedData.reframes) {
        updates.ai_reframes = {
          ...editedData.reframes,
          user_edited: true,
          last_edited_at: new Date().toISOString()
        };
      }
      if (editedData.learning_outcomes) {
        updates.ai_learning_outcomes = {
          ...editedData.learning_outcomes,
          user_edited: true,
          last_edited_at: new Date().toISOString()
        };
      }
      if (editedData.structure) {
        updates.ai_structure = {
          ...editedData.structure,
          user_edited: true,
          last_edited_at: new Date().toISOString()
        };
      }
      if (editedData.competencies) {
        updates.ai_competencies = {
          ...editedData.competencies,
          user_edited: true,
          last_edited_at: new Date().toISOString()
        };
      }
      if (editedData.roles) {
        updates.ai_roles = {
          ...editedData.roles,
          user_edited: true,
          last_edited_at: new Date().toISOString()
        };
      }

      // Update node in backend
      await api.put(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}`, updates);
      
      // Update local state
      if (typeof onChange === 'function') {
        onChange(id, { ...d, ...updates });
      }
      
      // Reset edit tracking
      setHasEdits(false);
      setEditedData({
        core_outcomes: null,
        reframes: null,
        learning_outcomes: null,
        structure: null,
        competencies: null,
        roles: null
      });
      
      setSnackbar({ open: true, message: '✅ Module saved successfully!', severity: 'success' });

    } catch (error) {

      setSnackbar({ open: true, message: '❌ Failed to save module', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [d, editedData, id, onChange]);

  const handleGenerateReframeConcepts = async (retryCount = 0, autoGenerateWording = true) => {
    if (!d._dbId || !d._qualificationId) return;
    // Prevent AI calls for orientation nodes
    if (d.is_orientation === true || d.uses_curriculum_doc === true) {
      setSnackbar({ open: true, message: 'Orientation nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    if (d.is_eisa === true) {
      setSnackbar({ open: true, message: 'EISA nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    setGeneratingReframeConcepts(true);
    setProgressMessage('Step 1/2: Generating reframe concepts...');
    try {
      setTimeout(() => setProgressMessage('Step 1/2: Analyzing module structure...'), 2000);
      setTimeout(() => setProgressMessage('Step 1/2: Identifying reframe types...'), 10000);
      
      const res = await api.post(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}/generate-reframe-concepts`);
      if (res.data.success && res.data.reframes) {
        if (res.data.node && res.data.node.data && typeof onChange === 'function') {
          // Preserve _moduleIndex and other metadata when updating
          onChange(id, { 
            ...res.data.node.data, 
            _moduleIndex: d._moduleIndex || 1,
            _dbId: d._dbId,
            _qualificationId: d._qualificationId,
            _outcomeId: d._outcomeId
          });
        }
        setProgressMessage('');
        const conceptCount = res.data.reframes.reframes?.length || 0;
        setSnackbar({ open: true, message: `Generated ${conceptCount} reframe concepts!${autoGenerateWording ? ' Generating wording...' : ''}`, severity: 'success' });
        
        // Automatically generate wording after concepts are created
        if (autoGenerateWording && conceptCount > 0) {
          // Wait a moment for the state to update, then generate wording
          await new Promise(r => setTimeout(r, 2000));
          try {
            await handleGenerateReframeWording();
          } catch (wordingError) {
            console.error('[handleGenerateReframeConcepts] Failed to auto-generate wording:', wordingError);
            // Don't throw - concepts were generated successfully, wording can be generated manually later
            setSnackbar({ 
              open: true, 
              message: 'Concepts generated, but wording generation failed. You can generate wording manually.', 
              severity: 'warning' 
            });
          }
        }
      }
    } catch (error) {
      console.error('[handleGenerateReframeConcepts] Error:', error);
      const isConnectionError = !error.response || error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK');
      
      if (isConnectionError) {
        setProgressMessage('');
        setSnackbar({ 
          open: true, 
          message: 'Backend server not available. Please start the backend server (npm run dev in backend folder).', 
          severity: 'error',
          autoHideDuration: 10000
        });
        throw new Error('Backend unavailable');
      } else if (retryCount < 2 && error.response?.status >= 500) {
        setProgressMessage(`Retrying... (${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 2000));
        return handleGenerateReframeConcepts(retryCount + 1, autoGenerateWording);
        }
        setProgressMessage('');
      setSnackbar({ open: true, message: `Failed to generate reframe concepts: ${error.response?.data?.message || error.message || 'Unknown error'}`, severity: 'error' });
      throw error;
    } finally {
      setGeneratingReframeConcepts(false);
      setProgressMessage('');
    }
  };

  const handleGenerateReframeWording = async (retryCount = 0) => {
    if (!d._dbId || !d._qualificationId) return;
    // Prevent AI calls for orientation nodes
    if (d.is_orientation === true || d.uses_curriculum_doc === true) {
      setSnackbar({ open: true, message: 'Orientation nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    if (d.is_eisa === true) {
      setSnackbar({ open: true, message: 'EISA nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    setGeneratingReframeWording(true);
    setProgressMessage('Step 2/2: Polishing reframe wording...');
    try {
      setTimeout(() => setProgressMessage('Step 2/2: Crafting compelling statements...'), 2000);
      setTimeout(() => setProgressMessage('Step 2/2: Refining explanations...'), 10000);
      
      const res = await api.post(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}/generate-reframe-wording`);
      if (res.data.success && res.data.reframes) {
        if (res.data.node && res.data.node.data && typeof onChange === 'function') {
          // Preserve _moduleIndex and other metadata when updating
          onChange(id, { 
            ...res.data.node.data, 
            _moduleIndex: d._moduleIndex || 1,
            _dbId: d._dbId,
            _qualificationId: d._qualificationId,
            _outcomeId: d._outcomeId
          });
        }
        setProgressMessage('');
        const wordingCount = res.data.reframes.reframes?.filter(rf => rf.wording?.statement).length || 0;
        setSnackbar({ open: true, message: `Generated wording for ${wordingCount} reframes!`, severity: 'success' });
      }
    } catch (error) {
      console.error('[handleGenerateReframeWording] Error:', error);
      if (retryCount < 2 && error.response?.status >= 500) {
        setProgressMessage(`Retrying... (${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 2000));
        return handleGenerateReframeWording(retryCount + 1);
      }
      setProgressMessage('');
      setSnackbar({ open: true, message: 'Failed to generate reframe wording', severity: 'error' });
    } finally {
      setGeneratingReframeWording(false);
      setProgressMessage('');
    }
  };

  const handleGenerateReframes = async (retryCount = 0) => {
    if (!d._dbId || !d._qualificationId) return;
    // Prevent AI calls for orientation nodes
    if (d.is_orientation === true || d.uses_curriculum_doc === true) {
      setSnackbar({ open: true, message: 'Orientation nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    if (d.is_eisa === true) {
      setSnackbar({ open: true, message: 'EISA nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    
    // Check if concepts exist without wording - if so, just generate wording
    const existingReframes = d.ai_reframes?.reframes || [];
    const hasConceptsWithoutWording = existingReframes.some(rf => 
      rf.concept && (!rf.wording || !rf.wording.statement)
    );
    
    if (hasConceptsWithoutWording && existingReframes.length > 0) {
      // Step 2 only: Generate wording for existing concepts
      return handleGenerateReframeWording(retryCount);
    }
    
    // Full two-step generation
    setGeneratingReframes(true);
    setProgressMessage('Starting two-step reframe generation...');
    try {
      // Step 1: Generate concepts (will auto-generate wording)
      await handleGenerateReframeConcepts(0, true);
      
      setProgressMessage('');
      setSnackbar({ open: true, message: 'Reframe generation complete!', severity: 'success' });
    } catch (error) {
      console.error('[handleGenerateReframes] Error:', error);
      setProgressMessage('');
      setSnackbar({ open: true, message: 'Failed to generate reframes', severity: 'error' });
    } finally {
      setGeneratingReframes(false);
      setProgressMessage('');
    }
  };

  const handleGenerateCoreOutcomes = async (retryCount = 0) => {
    if (!d._dbId || !d._qualificationId) {
      console.warn('[handleGenerateCoreOutcomes] Missing _dbId or _qualificationId:', { _dbId: d._dbId, _qualificationId: d._qualificationId });
      return;
    }
    // Prevent AI calls for orientation nodes
    if (d.is_orientation === true || d.uses_curriculum_doc === true) {
      setSnackbar({ open: true, message: 'Orientation nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    if (d.is_eisa === true) {
      setSnackbar({ open: true, message: 'EISA nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    
    // Skip health check here - it's already done in autoGenerateAll
    // The health check seems to cause timing issues with the backend
    // Just add a small delay to ensure backend is ready
    await new Promise(r => setTimeout(r, 300));
    
    setGeneratingCoreOutcomes(true);
    setProgressMessage('Uploading module data...');
    try {
      setTimeout(() => setProgressMessage('AI analyzing learning objectives...'), 2000);
      setTimeout(() => setProgressMessage('Structuring core outcomes...'), 10000);
      setTimeout(() => setProgressMessage('Mapping Bloom levels...'), 20000);
      
      // Use axios instead of fetch to benefit from token interceptor and automatic token refresh
      const res = await api.post(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}/generate-core-outcomes`);
      console.log('[handleGenerateCoreOutcomes] API call successful');
      console.log('[handleGenerateCoreOutcomes] Response:', res.data);
      if (res.data.success && res.data.core_outcomes) {
        // Backend saved it, now update local state with returned node data
        if (res.data.node && res.data.node.data && typeof onChange === 'function') {
          console.log('[handleGenerateCoreOutcomes] Updating node data:', res.data.node.data);
          // Preserve _moduleIndex and other metadata when updating
          onChange(id, { 
            ...res.data.node.data, 
            _moduleIndex: d._moduleIndex || 1,
            _dbId: d._dbId,
            _qualificationId: d._qualificationId,
            _outcomeId: d._outcomeId
          });
        } else {
          console.warn('[handleGenerateCoreOutcomes] No node data in response:', res.data);
        }
        setProgressMessage('');
        setSnackbar({ open: true, message: `Generated ${res.data.core_outcomes.outcomes?.length || 0} core outcomes!`, severity: 'success' });
        // Don't auto-switch tabs - let user control
      } else {
        console.warn('[handleGenerateCoreOutcomes] Response missing success or core_outcomes:', res.data);
        setSnackbar({ open: true, message: 'Response missing expected data', severity: 'warning' });
      }
    } catch (error) {
        console.error('[handleGenerateCoreOutcomes] Error details:', {
          message: error.message,
          code: error.code,
          response: error.response?.status,
          responseData: error.response?.data,
          config: error.config?.url,
          stack: error.stack
        });
        const isConnectionError = !error.response || error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK') || error.message?.includes('Failed to fetch');
        
        if (isConnectionError) {
          setProgressMessage('');
          setSnackbar({ 
            open: true, 
            message: `Backend server not available (${error.code || error.message}). Please start the backend server (npm run dev in backend folder).`, 
            severity: 'error',
            autoHideDuration: 10000
          });
          // Throw to stop auto-generation sequence
          throw new Error('Backend unavailable');
        }
        
        // Check for authentication errors (401/403) - token may have expired
        if (error.response?.status === 401 || error.response?.status === 403) {
          setProgressMessage('');
          setSnackbar({ 
            open: true, 
            message: 'Authentication failed. Please refresh the page and try again.', 
            severity: 'error',
            autoHideDuration: 10000
          });
          // Don't retry on auth errors - user needs to refresh/login
          throw error;
        } else if (error.response?.status === 500 && retryCount < 2) {
        setProgressMessage(`Retrying... (attempt ${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 3000));
        return handleGenerateCoreOutcomes(retryCount + 1);
        } else {
      setProgressMessage('');
          setSnackbar({ 
            open: true, 
            message: `Failed to generate core outcomes: ${error.response?.data?.message || error.message || 'Unknown error'}`, 
            severity: 'error' 
          });
          // Throw to stop auto-generation sequence on non-retryable errors
          throw error;
        }
    } finally {
      setGeneratingCoreOutcomes(false);
      setProgressMessage('');
    }
  };

  const handleGenerateLearningOutcomes = async (retryCount = 0) => {
    if (!d._dbId || !d._qualificationId) return;
    // Prevent AI calls for orientation nodes
    if (d.is_orientation === true || d.uses_curriculum_doc === true) {
      setSnackbar({ open: true, message: 'Orientation nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    if (d.is_eisa === true) {
      setSnackbar({ open: true, message: 'EISA nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    setGeneratingLearningOutcomes(true);
    setProgressMessage('Uploading module data...');
    try {
      setTimeout(() => setProgressMessage('AI extracting competencies...'), 2000);
      setTimeout(() => setProgressMessage('Generating detailed outcomes...'), 10000);
      setTimeout(() => setProgressMessage('Creating assessment criteria...'), 20000);
      
      const res = await api.post(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}/generate-learning-outcomes`);
      console.log('[handleGenerateLearningOutcomes] Response:', res.data);
      if (res.data.success && res.data.learning_outcomes) {
        // Backend saved it, now update local state with returned node data
        if (res.data.node && res.data.node.data && typeof onChange === 'function') {
          console.log('[handleGenerateLearningOutcomes] Updating node data:', res.data.node.data);
          // Preserve _moduleIndex and other metadata when updating
          onChange(id, { 
            ...res.data.node.data, 
            _moduleIndex: d._moduleIndex || 1,
            _dbId: d._dbId,
            _qualificationId: d._qualificationId,
            _outcomeId: d._outcomeId
          });
        } else {
          console.warn('[handleGenerateLearningOutcomes] No node data in response:', res.data);
        }
        setProgressMessage('');
        setSnackbar({ open: true, message: `Generated ${res.data.learning_outcomes.outcomes?.length || 0} learning outcomes!`, severity: 'success' });
        // Don't auto-switch tabs
      } else {
        console.warn('[handleGenerateLearningOutcomes] Response missing success or learning_outcomes:', res.data);
        setSnackbar({ open: true, message: 'Response missing expected data', severity: 'warning' });
      }
    } catch (error) {
        console.error('[handleGenerateLearningOutcomes] Error:', error);
        const isConnectionError = !error.response || error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK');
        
        if (isConnectionError) {
          setProgressMessage('');
          setSnackbar({ 
            open: true, 
            message: 'Backend server not available. Please start the backend server (npm run dev in backend folder).', 
            severity: 'error',
            autoHideDuration: 10000
          });
          // Throw to stop auto-generation sequence
          throw new Error('Backend unavailable');
        } else if (error.response?.status === 500 && retryCount < 2) {
        setProgressMessage(`Retrying... (attempt ${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 3000));
        return handleGenerateLearningOutcomes(retryCount + 1);
        } else {
      setProgressMessage('');
          setSnackbar({ 
            open: true, 
            message: `Failed to generate learning outcomes: ${error.response?.data?.message || error.message || 'Unknown error'}`, 
            severity: 'error' 
          });
          // Throw to stop auto-generation sequence on non-retryable errors
          throw error;
        }
    } finally {
      setGeneratingLearningOutcomes(false);
      setProgressMessage('');
    }
  };

  const handleGenerateStructure = async (retryCount = 0) => {
    if (!d._dbId || !d._qualificationId) return;
    // Prevent AI calls for orientation nodes
    if (d.is_orientation === true || d.uses_curriculum_doc === true) {
      setSnackbar({ open: true, message: 'Orientation nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    if (d.is_eisa === true) {
      setSnackbar({ open: true, message: 'EISA nodes use static content - no AI generation needed', severity: 'info' });
      return;
    }
    setGeneratingStructure(true);
    setProgressMessage('Uploading module data...');
    try {
      setTimeout(() => setProgressMessage('AI analyzing module structure...'), 2000);
      setTimeout(() => setProgressMessage('Organizing content hierarchy...'), 10000);
      setTimeout(() => setProgressMessage('Creating learning pathways...'), 20000);
      
      const res = await api.post(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}/generate-structure`);
      if (res.data.success && res.data.structure) {
        // Backend saved it, now update local state with returned node data
        if (res.data.node && res.data.node.data && typeof onChange === 'function') {
          // Preserve _moduleIndex and other metadata when updating
          onChange(id, { 
            ...res.data.node.data, 
            _moduleIndex: d._moduleIndex || 1,
            _dbId: d._dbId,
            _qualificationId: d._qualificationId,
            _outcomeId: d._outcomeId
          });
        }
        setProgressMessage('');
        setSnackbar({ open: true, message: 'Generated module structure!', severity: 'success' });
        // Don't auto-switch tabs
      }
    } catch (error) {
      console.error('[handleGenerateStructure] Error:', error);
      const isConnectionError = !error.response || error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK');
      
      if (isConnectionError) {
        setProgressMessage('');
        setSnackbar({ 
          open: true, 
          message: 'Backend server not available. Please start the backend server (npm run dev in backend folder).', 
          severity: 'error',
          autoHideDuration: 10000
        });
        throw new Error('Backend unavailable');
      } else if (error.response?.status === 500 && retryCount < 2) {
        setProgressMessage(`Retrying... (attempt ${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 3000));
        return handleGenerateStructure(retryCount + 1);
      }
      setProgressMessage('');
      setSnackbar({ open: true, message: `Failed to generate structure: ${error.response?.data?.message || error.message || 'Unknown error'}`, severity: 'error' });
      throw error;
    } finally {
      setGeneratingStructure(false);
      setProgressMessage('');
    }
  };

  const handleGenerateCompetencies = async (retryCount = 0) => {
    if (!d._dbId || !d._qualificationId) return;
    // Prevent AI calls for orientation nodes
    if (d.is_orientation === true || d.uses_curriculum_doc === true) {
      setSnackbar({ open: true, message: 'Orientation nodes do not have competencies', severity: 'info' });
      return;
    }
    if (d.is_eisa === true) {
      setSnackbar({ open: true, message: 'EISA nodes use static content - competencies not applicable', severity: 'info' });
      return;
    }
    setGeneratingCompetencies(true);
    setProgressMessage('Uploading module data...');
    try {
      setTimeout(() => setProgressMessage('AI identifying competencies...'), 2000);
      setTimeout(() => setProgressMessage('Mapping skills and knowledge...'), 10000);
      setTimeout(() => setProgressMessage('Defining proficiency levels...'), 20000);
      
      const res = await api.post(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}/generate-competencies`);
      if (res.data.success && res.data.competencies) {
        // Backend saved it, now update local state with returned node data
        if (res.data.node && res.data.node.data && typeof onChange === 'function') {
          // Preserve _moduleIndex and other metadata when updating
          onChange(id, { 
            ...res.data.node.data, 
            _moduleIndex: d._moduleIndex || 1,
            _dbId: d._dbId,
            _qualificationId: d._qualificationId,
            _outcomeId: d._outcomeId
          });
        }
        setProgressMessage('');
        setSnackbar({ open: true, message: 'Generated competencies!', severity: 'success' });
        // Don't auto-switch tabs
      }
    } catch (error) {
      console.error('[handleGenerateCompetencies] Error:', error);
      const isConnectionError = !error.response || error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK');
      
      if (isConnectionError) {
        setProgressMessage('');
        setSnackbar({ 
          open: true, 
          message: 'Backend server not available. Please start the backend server (npm run dev in backend folder).', 
          severity: 'error',
          autoHideDuration: 10000
        });
        throw new Error('Backend unavailable');
      } else if (error.response?.status === 500 && retryCount < 2) {
        setProgressMessage(`Retrying... (attempt ${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 3000));
        return handleGenerateCompetencies(retryCount + 1);
      }
      setProgressMessage('');
      setSnackbar({ open: true, message: `Failed to generate competencies: ${error.response?.data?.message || error.message || 'Unknown error'}`, severity: 'error' });
      throw error;
    } finally {
      setGeneratingCompetencies(false);
      setProgressMessage('');
    }
  };

  const handleGenerateRoles = async (retryCount = 0) => {
    if (!d._dbId || !d._qualificationId) return;
    setGeneratingRoles(true);
    setProgressMessage('Uploading module data...');
    try {
      setTimeout(() => setProgressMessage('AI identifying roles...'), 2000);
      setTimeout(() => setProgressMessage('Mapping responsibilities...'), 10000);
      setTimeout(() => setProgressMessage('Defining stakeholders...'), 20000);
      
      const res = await api.post(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}/generate-roles`);
      if (res.data.success && res.data.roles) {
        // Backend saved it, now update local state with returned node data
        if (res.data.node && res.data.node.data && typeof onChange === 'function') {
          // Preserve _moduleIndex and other metadata when updating
          onChange(id, { 
            ...res.data.node.data, 
            _moduleIndex: d._moduleIndex || 1,
            _dbId: d._dbId,
            _qualificationId: d._qualificationId,
            _outcomeId: d._outcomeId
          });
        }
        setProgressMessage('');
        setSnackbar({ open: true, message: 'Generated roles!', severity: 'success' });
        // Don't auto-switch tabs
      }
    } catch (error) {
      if (error.response?.status === 500 && retryCount < 2) {

        setProgressMessage(`Retrying... (attempt ${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 3000));
        return handleGenerateRoles(retryCount + 1);
      }
      setProgressMessage('');
      setSnackbar({ open: true, message: 'Failed to generate roles', severity: 'error' });

    } finally {
      setGeneratingRoles(false);
      setProgressMessage('');
    }
  };

  const handleTabChange = useCallback((event, newValue) => {
    event?.stopPropagation(); // Prevent React Flow from capturing the click
    setActiveTab(newValue);

  }, [activeTab]);

  const handlePreviewAIJson = useCallback(async () => {
    if (!d._dbId || !d._qualificationId) return;
    
    try {
      const res = await api.get(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}/ai-json-preview`);
      setAIJsonPreview(res.data);
      setOpenAIJson(true);
    } catch (error) {

      setSnackbar({ open: true, message: 'Failed to load AI JSON preview', severity: 'error' });
    }
  }, [d._dbId, d._qualificationId]);

  const handleRegenerateItem = useCallback(async (type, index) => {

    setSnackbar({ 
      open: true, 
      message: `Individual regeneration coming soon! (${type} #${index + 1})`, 
      severity: 'info' 
    });
  }, [d._qualificationId, d._dbId]);

  const handleClearAll = useCallback(async () => {
    if (!window.confirm('Clear ALL generated AI content for this module? This cannot be undone!')) {
      return;
    }
    
    try {

      const isOrientation = d.is_orientation === true || d.uses_curriculum_doc === true;
      const cleared = {
        ...d,
        ai_core_outcomes: null,
        ai_reframes: null,
        ai_learning_outcomes: null,
        ai_structure: null,
        ai_competencies: null,
        // Only clear roles for Orientation modules
        ...(isOrientation && { ai_roles: null })
      };
      
      // Save cleared data to backend
      await api.put(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}`, { data: cleared });
      
      // Update local state
      if (typeof onChange === 'function') onChange(id, cleared);
      
      setSnackbar({ 
        open: true, 
        message: '🗑️ All AI content cleared! Refresh to regenerate.', 
        severity: 'success',
        autoHideDuration: 4000
      });
      

    } catch (error) {

      setSnackbar({ 
        open: true, 
        message: '❌ Failed to clear content', 
        severity: 'error',
        autoHideDuration: 4000
      });
    }
  }, [d, d._dbId, d._qualificationId, id, onChange]);

  return (
    <Box sx={{ 
      position: 'relative', 
      width: 1200,
      minHeight: 600,
      maxHeight: 800,
      background: '#fff', 
      borderRadius: 3, 
      boxShadow: selected ? '0 0 0 3px #7c3aed' : '0 1px 6px rgba(0,0,0,0.15)', 
      overflow: 'hidden', 
      border: '1px solid #eee',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Handle id="l" type="target" position={Position.Left} style={{ background: '#7c3aed', width: 10, height: 10, border: '2px solid #fff' }} />
      <Handle id="r" type="source" position={Position.Right} style={{ background: '#7c3aed', width: 10, height: 10, border: '2px solid #fff' }} />
      
      {/* Header */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fafafa', borderBottom: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{`Module ${moduleIndex} - ${d.title || 'Untitled'}`}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#7c3aed' }}>
            {d.is_orientation ? 'Orientation' : ''}
            {d.is_eisa ? 'EISA Preparation' : ''}
          </Typography>
        </Box>
        {!isReadOnly && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!d.is_orientation && !d.uses_curriculum_doc && !d.is_eisa && (
              <Button
                variant="outlined"
                color="info"
                size="small"
                onClick={handlePreviewAIJson}
                sx={{ fontSize: '0.75rem' }}
              >
                View AI Input JSON
              </Button>
            )}
            {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleClearAll}
              sx={{ fontSize: '0.75rem' }}
            >
              Clear All AI Content
            </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Tabs 
        value={activeTab >= 0 && activeTab <= ((d.is_orientation || d.uses_curriculum_doc) && !d.is_eisa ? 4 : 5) ? activeTab : 0} // Ensure valid tab index
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0,
          '& .MuiTab-root': { 
            textTransform: 'none', 
            fontWeight: 600, 
            minHeight: 48,
            pointerEvents: 'auto' // Ensure tabs are clickable
          }
        }}
      >
        <Tab 
          value={0}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {generatingCoreOutcomes && <CircularProgress size={14} />}
              <Badge badgeContent={coreOutcomesData?.outcomes?.length || 0} color="primary">
                Core Message
              </Badge>
            </Box>
          } 
        />
        <Tab 
          value={1}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {generatingLearningOutcomes && <CircularProgress size={14} />}
              <Badge badgeContent={learningOutcomesData?.outcomes?.length || 0} color="info">
                Learning Outcomes
              </Badge>
            </Box>
          } 
        />
        <Tab 
          value={2}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {generatingReframes && <CircularProgress size={14} />}
              <Badge badgeContent={reframesData?.reframes?.length || 0} color="secondary">
                Reframes
              </Badge>
            </Box>
          } 
        />
        <Tab 
          value={3}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {generatingStructure && <CircularProgress size={14} />}
              <Badge badgeContent={structureData ? 1 : 0} color="success">
                Structure
              </Badge>
            </Box>
          } 
        />
        {/* Competencies tab - NOT for Orientation module */}
        {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
        <Tab 
          value={4}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {generatingCompetencies && <CircularProgress size={14} />}
              <Badge badgeContent={competenciesData ? 1 : 0} color="warning">
                Competencies
              </Badge>
            </Box>
          } 
        />
        )}
        {/* Duration tab - for all modules */}
        <Tab 
          value={(d.is_orientation || d.uses_curriculum_doc) && !d.is_eisa ? 6 : 5}
          label="Duration"
        />
      </Tabs>

      {/* Content Area with fixed height to prevent resize issues */}
      <Box 
        sx={{ 
          flex: 1, 
          overflow: 'hidden', // CRITICAL: Don't scroll here - let TabPanel handle it!
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Tab Panel 0: Core Message (AI Generated) */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: 2 }}>
            {!coreOutcomesData || !shouldRenderCoreMessage ? (
              <>
                {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                <EmptyState
                  title="Core Message Not Generated"
                  description="Generate the AI-powered core message for this module to see the main learning outcome and supporting points."
                  onGenerate={handleGenerateCoreOutcomes}
                  generating={generatingCoreOutcomes}
                  progressMessage={progressMessage}
                  isReadOnly={isReadOnly}
                />
                )}
                {coreOutcomesData && !shouldRenderCoreMessage && !(d.is_orientation || d.uses_curriculum_doc) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {`Latest generation produced ${coreOutcomeLabel} instead of Core Message. The data contains learning outcomes (statements) rather than a core message. Please regenerate to produce a Core Message payload with core_message and supporting_points fields.`}
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={generatingCoreOutcomes ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={handleGenerateCoreOutcomes}
                        disabled={generatingCoreOutcomes}
                      >
                        Regenerate Core Message
                      </Button>
                    </Box>
                  </Alert>
                )}
                {(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && !coreOutcomesData && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Core Message will be displayed here once the node is initialized.
                  </Alert>
                )}
              </>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Core Message
                  </Typography>
                  {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={generatingCoreOutcomes ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleGenerateCoreOutcomes}
                    disabled={generatingCoreOutcomes}
                  >
                    Regenerate
                  </Button>
                  )}
                </Box>
                <OutcomesDisplay 
                  data={
                    // Transform static content structure to match OutcomesDisplay expectations
                    // Static content has { core_message, supporting_points } directly
                    // OutcomesDisplay expects { outcomes: [{ core_message, supporting_points }] }
                    coreOutcomesData && coreOutcomesData.core_message && !coreOutcomesData.outcomes
                      ? { outcomes: [{ 
                          core_message: coreOutcomesData.core_message,
                          supporting_points: coreOutcomesData.supporting_points || [],
                          confidence: coreOutcomesData.confidence,
                          evidence_nodes: coreOutcomesData.evidence_nodes || []
                        }] }
                      : (coreOutcomesData?.outcomes ? coreOutcomesData : null)
                  }
                  type="core"
                  isOrientation={(d.is_orientation || d.uses_curriculum_doc) || d.is_eisa}
                  onEdit={(newData) => {
                    // Transform back from OutcomesDisplay format to storage format
                    if (newData?.outcomes && newData.outcomes.length > 0) {
                      const transformed = {
                        ...newData,
                        core_message: newData.outcomes[0].core_message,
                        supporting_points: newData.outcomes[0].supporting_points || []
                      };
                      delete transformed.outcomes;
                      handleDataEdit('core_outcomes', transformed);
                    } else {
                      handleDataEdit('core_outcomes', newData);
                    }
                  }}
                  userEdited={coreOutcomesData?.user_edited}
                />
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab Panel 1: Learning Outcomes */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: 2 }}>
            {!learningOutcomesData ? (
              <>
                {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
              <EmptyState
                title="Learning Outcomes Not Generated"
                description="Generate detailed learning outcomes with assessment criteria and evidence requirements."
                onGenerate={handleGenerateLearningOutcomes}
                generating={generatingLearningOutcomes}
                progressMessage={progressMessage}
                isReadOnly={isReadOnly}
              />
                )}
                {(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Learning Outcomes will be displayed here once the node is initialized.
                  </Alert>
                )}
              </>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Learning Outcomes ({learningOutcomesData.outcomes?.length || 0})
                  </Typography>
                  {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={generatingLearningOutcomes ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleGenerateLearningOutcomes}
                    disabled={generatingLearningOutcomes}
                  >
                    Regenerate
                  </Button>
                  )}
                </Box>
                <OutcomesDisplay 
                  data={learningOutcomesData} 
                  type="learning"
                  onEdit={(newData) => handleDataEdit('learning_outcomes', newData)}
                  userEdited={learningOutcomesData?.user_edited}
                />
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab Panel 2: Reframes */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ p: 2 }}>
            {!reframesData ? (
              <>
                {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
              <EmptyState
                title="Reframes Not Generated"
                description="Generate strategic reframes to explore alternative perspectives and opportunities for this module."
                onGenerate={handleGenerateReframes}
                generating={generatingReframes}
                progressMessage={progressMessage}
                isReadOnly={isReadOnly}
              />
                )}
                {(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Reframes will be displayed here once the node is initialized.
                  </Alert>
                )}
              </>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Strategic Reframes ({reframesData.reframes?.length || 0})
                  </Typography>
                  {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                    <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                        size="small"
                        startIcon={generatingReframeConcepts ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={handleGenerateReframeConcepts}
                        disabled={generatingReframeConcepts || generatingReframeWording || generatingReframes}
                      >
                        {generatingReframeConcepts ? 'Generating...' : 'Regenerate Concepts'}
                      </Button>
                      {reframesData?.reframes && reframesData.reframes.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={generatingReframeWording ? <CircularProgress size={16} /> : <RefreshIcon />}
                          onClick={handleGenerateReframeWording}
                          disabled={generatingReframeConcepts || generatingReframeWording || generatingReframes}
                        >
                          {generatingReframeWording ? 'Generating...' : 'Regenerate Wording'}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                    size="small"
                    startIcon={generatingReframes ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleGenerateReframes}
                        disabled={generatingReframeConcepts || generatingReframeWording || generatingReframes}
                  >
                        {generatingReframes ? 'Generating...' : 'Generate All'}
                  </Button>
                    </Stack>
                  )}
                </Box>
                <ReframesDisplay 
                  data={reframesData} 
                  onRegenerateItem={handleRegenerateItem}
                  onEdit={(newData) => handleDataEdit('reframes', newData)}
                  userEdited={reframesData?.user_edited}
                  onGenerateConcepts={handleGenerateReframeConcepts}
                  onGenerateWording={handleGenerateReframeWording}
                  generatingConcepts={generatingReframeConcepts}
                  generatingWording={generatingReframeWording}
                  isOrientation={(d.is_orientation || d.uses_curriculum_doc) || d.is_eisa}
                />
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab Panel 3: Structure */}
        <TabPanel value={activeTab} index={3}>
          <Box sx={{ p: 2 }}>
            {!structureData ? (
              <>
                {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
              <EmptyState
                title="Module Structure Not Generated"
                description="Generate the AI-powered module structure to see organized content hierarchy and learning pathways."
                onGenerate={handleGenerateStructure}
                generating={generatingStructure}
                progressMessage={progressMessage}
                isReadOnly={isReadOnly}
              />
                )}
                {(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Module Structure will be displayed here once the node is initialized.
                  </Alert>
                )}
              </>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Module Structure
                  </Typography>
                  {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={generatingStructure ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleGenerateStructure}
                    disabled={generatingStructure}
                  >
                    Regenerate
                  </Button>
                  )}
                </Box>
                <StructureDisplay 
                  data={structureData}
                  onEdit={(newData) => handleDataEdit('structure', newData)}
                  userEdited={structureData?.user_edited}
                  isReadOnly={isReadOnly}
                />
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab Panel 4: Competencies - NOT for Orientation */}
        {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
        <TabPanel value={activeTab} index={4}>
          <Box sx={{ p: 2 }}>
            {!competenciesData ? (
              <>
                {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
              <EmptyState
                title="Competencies Not Generated"
                description="Generate the AI-powered competencies analysis to see skills, knowledge, and proficiency levels for this module."
                onGenerate={handleGenerateCompetencies}
                generating={generatingCompetencies}
                progressMessage={progressMessage}
              />
                )}
                {(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Competencies are not applicable for this node.
                  </Alert>
                )}
              </>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Competencies
                  </Typography>
                  {!(d.is_orientation || d.uses_curriculum_doc || d.is_eisa) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={generatingCompetencies ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleGenerateCompetencies}
                    disabled={generatingCompetencies}
                  >
                    Regenerate
                  </Button>
                  )}
                </Box>
                <CompetenciesDisplay 
                  data={competenciesData}
                  onEdit={(newData) => handleDataEdit('competencies', newData)}
                  userEdited={competenciesData?.user_edited}
                />
              </Box>
            )}
          </Box>
        </TabPanel>
        )}

        {/* Duration Tab - for all modules */}
        <TabPanel value={activeTab} index={(d.is_orientation || d.uses_curriculum_doc) && !d.is_eisa ? 4 : 5}>
            <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
                Module Duration
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Duration (Weeks)"
                  type="number"
                  value={d.durationWeeks ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseInt(e.target.value);
                    if (typeof onChange === 'function') {
                      onChange(id, { ...d, durationWeeks: value });
                    }
                  }}
                  size="small"
                  helperText="Number of weeks for this module"
                  sx={{ bgcolor: 'white' }}
                />
                <TextField
                  label="Active Hours (Total)"
                  type="number"
                  value={d.active_hours ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseInt(e.target.value);
                    if (typeof onChange === 'function') {
                      onChange(id, { ...d, active_hours: value });
                    }
                  }}
                  size="small"
                  helperText="Total active contact hours for this module"
                  sx={{ bgcolor: 'white' }}
                />
                
                {/* Calculated values display */}
                {(d.durationWeeks && d.active_hours) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#e0f2fe', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#0369a1' }}>
                      Calculated Distribution
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          Hours per Week:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {(d.active_hours / d.durationWeeks).toFixed(1)} hours/week
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          Hours per Day (5-day week):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {(d.active_hours / (d.durationWeeks * 5)).toFixed(1)} hours/day
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          Hours per Day (7-day week):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {(d.active_hours / (d.durationWeeks * 7)).toFixed(1)} hours/day
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}
                
                {/* Save button */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      if (!d._dbId || !d._qualificationId) {
                        setSnackbar({ open: true, message: 'Cannot save: Missing node ID', severity: 'error' });
                        return;
                      }
                      try {
                        await api.put(`/qualifications/${d._qualificationId}/storyboard-nodes/${d._dbId}`, {
                          data: {
                            ...d,
                            durationWeeks: d.durationWeeks ?? null,
                            active_hours: d.active_hours ?? null
                          }
                        });
                        setSnackbar({ open: true, message: '✅ Duration settings saved!', severity: 'success', autoHideDuration: 2000 });
                      } catch (error) {
                        console.error('[Duration] Save error:', error);
                        setSnackbar({ open: true, message: '❌ Failed to save duration settings', severity: 'error' });
                      }
                    }}
                    disabled={isReadOnly}
                  >
                    Save Duration Settings
                    </Button>
                  </Box>
              </Stack>
                </Box>
            </Box>
          </TabPanel>

        {/* Old Overview content removed - was Tab Panel 0 */}
        {/* Keeping below for reference if needed */}
        {false && (
        <TabPanel value={activeTab} index={999}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box sx={{ bgcolor: '#f3e8ff', borderRadius: 2, p: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Orientation - Choosing how you plAy</Typography>
            <TextField 
              value={d.title || ''} 
              onChange={handleField('title')} 
              placeholder="Module title" 
              size="small" 
              fullWidth 
            />
          </Box>
          <Box sx={{ bgcolor: '#fbe7ff', borderRadius: 2, p: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Core Message</Typography>
            <TextField 
              value={d.messiahMessage || ''} 
              onChange={handleField('messiahMessage')} 
              placeholder="Message" 
              size="small" 
              fullWidth 
              multiline 
              minRows={3} 
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1, bgcolor: '#f8fafc', borderRadius: 2, p: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Key Learnings & Outcomes</Typography>
            <TextField 
              value={d.keyLearningsText || ''} 
              onChange={handleField('keyLearningsText')} 
              placeholder="Enter key learnings (one per line)" 
              size="small" 
              fullWidth 
              multiline 
              minRows={4} 
            />
          </Box>
          <Box sx={{ flex: 1, bgcolor: '#f5f3ff', borderRadius: 2, p: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Scope</Typography>
            <TextField 
              value={d.scopeText || ''} 
              onChange={handleField('scopeText')} 
              placeholder="Scope details" 
              size="small" 
              fullWidth 
              multiline 
              minRows={4} 
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Stack direction="row" spacing={1}>
            <Chip size="small" label="3 weeks" />
            <Chip size="small" label="VMS + SIM" />
            <Chip size="small" label="Formative" />
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={() => {
              if (typeof onOpenTasks === 'function') {
                onOpenTasks({ title: d.title || `Module ${moduleIndex}`, assigned });
              }
            }}
          >
            View Assigned Tasks
          </Button>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={() => setOpenJson(true)}
          >
            View Full JSON
          </Button>
        </Box>
          </Box>
        </TabPanel>
        )}

        {/* OLD DUPLICATE TAB PANELS REMOVED - See new structure above at indices 0, 1, 2 */}
      </Box>

      {/* AI Input JSON Modal */}
      <Dialog open={openAIJson} onClose={() => setOpenAIJson(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          AI Input JSON Preview
          {d.is_orientation && <Chip label="Uses Curriculum Document" color="warning" size="small" sx={{ ml: 2 }} />}
        </DialogTitle>
        <DialogContent dividers>
          <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 600, overflow: 'auto' }}>
            {aiJsonPreview ? JSON.stringify(aiJsonPreview, null, 2) : 'Loading...'}
          </pre>
        </DialogContent>
      </Dialog>

      {/* JSON Modal */}
      <Dialog open={openJson} onClose={() => setOpenJson(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Complete Module Data</DialogTitle>
        <DialogContent dividers>
          <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify({
              id: d._dbId,
              title: d.title,
              description: d.description || d.text || null,
              elo_number: d.elo_number || null,
              suggested_module_title: d.suggested_module_title || null,
              credits: d._eloData?.credits ?? d.credits ?? d.derived_credits ?? null,
              nqfLevel: d._eloData?.nqfLevel ?? d.nqf_level ?? d.nqfLevel ?? null,
              notionalHours: d._eloData?.notionalHours ?? d.notional_hours ?? null,
              activeHours: d._eloData?.activeHours ?? d.active_hours ?? null,
              evidenceType: d.evidence_type ?? null,
              assigned: {
                KT: Array.isArray(assigned?.KT) ? assigned.KT : [],
                PS: Array.isArray(assigned?.PS) ? assigned.PS : [],
                WE: Array.isArray(assigned?.WE) ? assigned.WE : []
              },
              assignment_meta: d.assignment_meta || {},
              knowledge_tasks: d.knowledge_tasks || [],
              practical_work: d.practical_work || [],
              workplace_experience: d.workplace_experience || [],
              ai_content: {
                reframes: reframesData,
                core_outcomes: coreOutcomesData,
                learning_outcomes: learningOutcomesData
              }
            }, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={snackbar.autoHideDuration || 4000} 
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

